import { useCallback, useEffect, useRef, useState } from 'react';
import { LoggerUtil } from '@axiom/ui';

import type { ChatHistoryItem, MatchingCandidate } from '../../api/chat-api';
import { ChatApi } from '../../api/chat-api';
import { ChatLogUtil } from '../../utils/chat-log-util';
import { DrawerView } from '../../enums/drawer-view';
import type { ChatLogEntry } from '../../types/chat.types';

import { useSparkStatus } from './SparkStatusProvider';

/**
 * useSparkConversation - I own the chat that is open: its id, its transcript, its candidate set
 * and its scroll cues. ONLY concerned with what is on screen - no sending.
 */
export const useSparkConversation = () => {
  const { setIsLoading, setNotice } = useSparkStatus();

  const [chatLog, setChatLog] = useState<ChatLogEntry[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [drawerView, setDrawerView] = useState<DrawerView>(DrawerView.CLOSED);
  const [matchingCandidates, setMatchingCandidates] = useState<
    MatchingCandidate[]
  >([]);

  const [pinnedPromptId, setPinnedPromptId] = useState<string | null>(null);
  const [restoredChatId, setRestoredChatId] = useState<string | null>(null);

  const loadedChatIdRef = useRef<string | null>(null);

  // The chat a send is for, readable when the send starts - a queued prompt runs long after
  // the render that queued it.
  const activeChatIdRef = useRef<string | null>(null);

  // The id the next send must quote or the server 409s it. A ref, for the reason above.
  const lastAnswerIdRef = useRef<string | null>(null);

  // Bumped by every navigation, so a response that outlives its screen can tell.
  const screenGenerationRef = useRef(0);

  const [arrivalCount, setArrivalCount] = useState(0);

  /*
    **Per conversation, and one-way.** A 422 says this chat can hold no more, which nothing but
    leaving it can undo - so it is set once and cleared only by `selectChat` or `resetScreen`,
    both of which are the reader going somewhere else.
  */
  const [isChatFull, setIsChatFull] = useState(false);

  /*
    The ids already accounted for, however they got here. A restore writes this without counting,
    which is what makes the announcement silent while still leaving a later answer able to tell that
    those people are not new.
  */
  const announcedCandidateIdsRef = useRef<string[]>([]);

  const candidateIdsOf = (candidates: MatchingCandidate[]) =>
    candidates.flatMap(({ candidateId }) => candidateId ?? []);

  /** Taken as seen without announcing anything - see `announcedCandidateIdsRef`. */
  const adoptCandidates = (candidates: MatchingCandidate[]) => {
    announcedCandidateIdsRef.current = candidateIdsOf(candidates);
    setMatchingCandidates(candidates);
  };

  /**
   * **The only path that announces.** An answer landing is the one event that means talent arrived;
   * a restore, a "New Chat" and the reader pressing a turn's own candidates button all adopt
   * quietly instead.
   *
   * **An empty set never replaces a populated one**: a follow-up matching nobody should not take
   * away the comparison the reader opened the drawer for - and it announces nothing either, since
   * nobody arrived.
   */
  const showMatchingCandidates = (candidates: MatchingCandidate[]) => {
    if (candidates.length === 0) return;

    const ids = candidateIdsOf(candidates);
    // An arrival is an id the last accounted-for set did not have, so a repeated answer is quiet.
    const arrived = ids.some(
      id => !announcedCandidateIdsRef.current.includes(id)
    );

    announcedCandidateIdsRef.current = ids;
    setMatchingCandidates(candidates);

    if (arrived) setArrivalCount(count => count + 1);
  };

  /*
    Opens the grid without narrowing it: a reader already in full view asked to see a particular
    turn's matches, not to be put back to the narrower grid. Adopted rather than announced - they
    pressed the button, so telling them something arrived says nothing they did not just do.
  */
  const showCandidates = useCallback((candidates: MatchingCandidate[]) => {
    adoptCandidates(candidates);
    setDrawerView(view =>
      view === DrawerView.CLOSED ? DrawerView.PARTIAL : view
    );
  }, []);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  /**
   * **What is on screen when a conversation is opened, and nothing else.** Emptying the queue and
   * saying what that cost the reader belong to `SparkSessionUtil.selectChat`.
   *
   * **`isFull` comes from the row, not from the load:** `chatState` is only on the history list.
   */
  const openChat = ({
    chatId,
    isFull = false,
  }: {
    chatId: ChatHistoryItem['id'] | null;
    isFull?: boolean;
  }) => {
    screenGenerationRef.current += 1;

    lastAnswerIdRef.current = null;

    /*
      `matchingCandidates` and `loadedChatIdRef` are deliberately left alone: the grid's set is
      replaced when the load lands, and the effect compares against the ref to decide whether to
      read at all.
    */
    setChatLog([]);
    setPinnedPromptId(null);
    setRestoredChatId(null);
    setIsChatFull(isFull);
    setActiveChatId(chatId);
  };

  /**
   * "New Chat" puts the screen back to empty. Both scroll signals go too: neither describes
   * anything on screen any more, and leaving `restoredChatId` set would send the next load of that
   * same conversation to the bottom without a fresh cue - or worse, not at all.
   */
  const resetScreen = () => {
    setChatLog([]);
    loadedChatIdRef.current = null;
    // Nothing accounted for on an empty screen, so the first answer's talent is genuinely new.
    adoptCandidates([]);
    /*
      The grid goes with its contents. Today "New Chat" cannot be reached while the grid is open -
      it lives in the left column, which is `visibility: hidden` by then - so this guards against
      the layout changing rather than against anything reachable now.
    */
    setDrawerView(DrawerView.CLOSED);
    setPinnedPromptId(null);
    setRestoredChatId(null);
    setIsChatFull(false);
    // For the reason `selectChat` clears it: an undismissable banner would outlive its chat.
    setNotice(null);
  };

  useEffect(() => {
    let stale = false;

    if (activeChatId && activeChatId !== loadedChatIdRef.current) {
      setIsLoading(true);
      // Refreshed first: the read is cached by endpoint and no write invalidates it.
      void ChatApi.loadChat({ chatId: activeChatId })
        .then(({ data }) => {
          if (stale) return;

          loadedChatIdRef.current = data.id;
          // The read is the other place the newest answer is learnt - restoring a
          // conversation has to leave this client able to add to it.
          lastAnswerIdRef.current = ChatLogUtil.latestAnswerId({
            messages: data.messages,
          });
          setChatLog(
            ChatLogUtil.toEntries({
              chatId: data.id,
              messages: data.messages,
              openAiFiles: data.openAiFiles,
            })
          );
          /*
            **Adopted, never announced.** Reopening a conversation is the reader asking to look at
            it, so its talent is already accounted for - the badge, the ring, the green pill and the
            peek all stay put.
          */
          adoptCandidates(
            ChatLogUtil.latestMatchingCandidates({ messages: data.messages })
          );
          setIsLoading(false);
          setPinnedPromptId(null);
          // Set in the same commit as the log, so the effect watching it in `Chat`
          // runs with the restored turns already in the DOM and a real height to
          // scroll to.
          setRestoredChatId(data.id);
        })
        .catch((error: unknown) => {
          if (stale) return;

          LoggerUtil.fireError(
            `Spark: failed to load chat ${activeChatId} - ${
              error instanceof Error ? error.message : JSON.stringify(error)
            }`
          );

          setIsLoading(false);
          const previousChatId = loadedChatIdRef.current;

          loadedChatIdRef.current = null;
          setActiveChatId(previousChatId);
          setNotice(
            "We couldn't open that chat. It may no longer exist, or it belongs to someone else."
          );
        });
    } else if (!activeChatId) {
      // The flag is cleared here because a discarded response never reaches its own `setIsLoading`.
      setIsLoading(false);
    }

    return () => {
      stale = true;
    };
  }, [activeChatId]);

  return {
    activeChatId,
    activeChatIdRef,
    arrivalCount,
    chatLog,
    drawerView,
    isChatFull,
    lastAnswerIdRef,
    loadedChatIdRef,
    matchingCandidates,
    pinnedPromptId,
    markChatFull: () => {
      setIsChatFull(true);
    },
    resetScreen,
    restoredChatId,
    screenGenerationRef,
    openChat,
    setActiveChatId,
    setChatLog,
    setDrawerView,
    setPinnedPromptId,
    setRestoredChatId,
    showCandidates,
    showMatchingCandidates,
  };
};
